
import { useEffect, useState } from 'react';
import { useBooking } from '@/contexts/BookingContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle, CalendarPlus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { formatDate } from '@/lib/utils';

const BookingComplete = () => {
  const navigate = useNavigate();
  const {
    selectedProvider,
    selectedService,
    selectedDate,
    selectedTime,
    selectedStaff,
    bookingReference,
    isBookingComplete,
    setIsBookingComplete,
    resetBooking,
    getTotalPrice,
    contactInfo
  } = useBooking();

  const [retries, setRetries] = useState(0);
  const [loading, setLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);

  // Handle missing booking data with retry mechanism
  useEffect(() => {
    // If we don't have booking data but are on the complete page, try to mark the booking as complete
    if (!isBookingComplete && window.location.pathname === '/booking/complete') {
      console.log("BookingComplete: No booking data found, marking as complete");
      setIsBookingComplete(true);
    }

    // If after a short delay we still don't have booking data, increment retry counter
    const timer = setTimeout(() => {
      if (!bookingReference || !selectedService) {
        console.log("BookingComplete: Still missing data, retry:", retries);
        if (retries < 3) {
          setRetries(prev => prev + 1);
        } else {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [bookingReference, selectedService, isBookingComplete, retries, setIsBookingComplete]);

  // Send booking confirmation email once we have all the necessary data
  useEffect(() => {
    if (
      !loading && 
      selectedProvider && 
      selectedService && 
      selectedDate && 
      selectedTime && 
      bookingReference && 
      contactInfo.email && 
      !emailSent && 
      !sendingEmail
    ) {
      sendBookingConfirmationEmail();
    }
  }, [loading, selectedProvider, selectedService, selectedDate, selectedTime, bookingReference, contactInfo.email, emailSent]);

  const sendBookingConfirmationEmail = async () => {
    try {
      setSendingEmail(true);
      
      // Format the date properly for the email
      const formattedDate = formatDate(selectedDate || new Date());
      
      // Calculate timestamp for the appointment (for reminders)
      const appointmentDate = new Date(selectedDate || new Date());
      const [hours, minutes] = (selectedTime || "00:00").split(':').map(Number);
      appointmentDate.setHours(hours, minutes, 0, 0);
      const timestamp = Math.floor(appointmentDate.getTime() / 1000);
      
      // Call the Edge Function to send the email
      const { data, error } = await supabase.functions.invoke('send-booking-confirmation', {
        body: {
          booking_id: bookingReference,
          customer_email: contactInfo.email,
          customer_name: contactInfo.name,
          provider_name: selectedProvider?.name,
          provider_address: selectedProvider?.adresse,
          service_name: selectedService?.name,
          booking_date: formattedDate,
          booking_time: selectedTime,
          staff_name: selectedStaff?.name,
          booking_timestamp: timestamp,
          provider_lat: selectedProvider?.standort?.latitude,
          provider_lng: selectedProvider?.standort?.longitude
        }
      });

      if (error) {
        console.error("Error sending booking confirmation:", error);
        toast({
          title: "Fehler beim Senden der Bestätigungsmail",
          description: "Die Email konnte nicht versendet werden. Wir arbeiten daran.",
          variant: "destructive"
        });
      } else {
        console.log("Booking confirmation email sent:", data);
        setEmailSent(true);
        toast({
          title: "Bestätigungs-Email versendet",
          description: `Eine Bestätigung wurde an ${contactInfo.email} gesendet.`,
        });
      }
    } catch (err) {
      console.error("Exception sending confirmation email:", err);
    } finally {
      setSendingEmail(false);
    }
  };

  // Generate ICS calendar file
  const generateICS = () => {
    if (!selectedDate || !selectedTime || !selectedService) return;

    // Format date and time for ICS
    const startDate = new Date(selectedDate);
    const [hours, minutes] = selectedTime.split(':').map(Number);
    startDate.setHours(hours, minutes, 0, 0);

    // Calculate end time based on service duration (in minutes)
    const endDate = new Date(startDate);
    endDate.setMinutes(endDate.getMinutes() + (selectedService.dauer || 60));

    // Format dates for ICS file
    const formatICSDate = (date: Date) => {
      return date.toISOString().replace(/-|:|\.\d+/g, '');
    };

    // Create ICS content
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//CHRONA//Booking//DE',
      'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      `UID:${bookingReference}@chrona.it`,
      `SUMMARY:${selectedService.name} bei ${selectedProvider?.name}`,
      `DESCRIPTION:Dein Termin für ${selectedService.name} mit ${selectedStaff?.name}. Referenz: ${bookingReference}`,
      `LOCATION:${selectedProvider?.adresse || 'Adresse nicht verfügbar'}`,
      `DTSTART:${formatICSDate(startDate)}`,
      `DTEND:${formatICSDate(endDate)}`,
      'BEGIN:VALARM',
      'ACTION:DISPLAY',
      'TRIGGER:-PT1H',
      'DESCRIPTION:Reminder',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR'
    ].join('\r\n');

    // Create and download file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `CHRONA_Buchung_${bookingReference}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // If loading or no booking data, show appropriate feedback
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-8">
        <div className="animate-pulse h-12 w-12 rounded-full bg-chrona-accent/30 mb-4"></div>
        <p className="text-chrona-text">Buchungsdetails werden geladen...</p>
      </div>
    );
  }

  // If after retries we still don't have booking data
  if (!selectedService || !bookingReference) {
    return (
      <Card className="w-full max-w-3xl mx-auto">
        <CardHeader>
          <CardTitle className="text-center">Keine Buchung gefunden oder Buchungsdaten unvollständig.</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center">
          <p className="mb-6 text-center text-chrona-gray-400">
            Es scheint, dass keine aktuelle Buchung vorliegt oder die Buchungsdaten unvollständig sind.
          </p>
          <Button 
            className="bg-chrona-accent hover:bg-chrona-accent-light text-white"
            onClick={() => {
              resetBooking();
              navigate('/booking');
            }}
          >
            Neue Buchung starten
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <motion.div 
      className="container mx-auto px-4 py-8" 
      initial={{ opacity: 0, y: 20 }} 
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-24 w-24 rounded-full bg-chrona-accent/20 mb-4">
            <CheckCircle size={48} className="text-chrona-accent" />
          </div>
          <h1 className="text-3xl font-bold text-chrona-text mb-2">Buchung erfolgreich!</h1>
          <p className="text-chrona-gray-400">Deine Buchungsreferenz: {bookingReference}</p>
        </div>
        
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Buchungsdetails</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h3 className="font-medium text-chrona-text">Dienstleistung</h3>
                <p className="text-chrona-gray-400">{selectedService?.name}</p>
              </div>
              <div>
                <h3 className="font-medium text-chrona-text">Anbieter</h3>
                <p className="text-chrona-gray-400">{selectedProvider?.name}</p>
              </div>
              <div>
                <h3 className="font-medium text-chrona-text">Mitarbeiter</h3>
                <p className="text-chrona-gray-400">{selectedStaff?.name || 'Nicht angegeben'}</p>
              </div>
              <div>
                <h3 className="font-medium text-chrona-text">Datum & Uhrzeit</h3>
                <p className="text-chrona-gray-400">
                  {selectedDate?.toLocaleDateString()} um {selectedTime} Uhr
                </p>
              </div>
              <div>
                <h3 className="font-medium text-chrona-text">Kosten</h3>
                <p className="text-chrona-accent font-medium">{getTotalPrice().toFixed(2)} €</p>
              </div>
              <div>
                <h3 className="font-medium text-chrona-text">Kontaktdaten</h3>
                <p className="text-chrona-gray-400">{contactInfo.name}, {contactInfo.email}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col md:flex-row gap-4 items-center justify-center mb-8">
          <Button 
            className="bg-chrona-accent hover:bg-chrona-accent-light text-white flex items-center gap-2 w-full md:w-auto"
            onClick={generateICS}
          >
            <CalendarPlus size={18} />
            In Kalender speichern
          </Button>
          <Button 
            variant="outline" 
            className="border-chrona-gray-700 hover:bg-chrona-gray-700/10 w-full md:w-auto"
            onClick={() => {
              resetBooking();
              navigate('/');
            }}
          >
            Zur Startseite
          </Button>
        </div>

        <div className="text-center text-sm text-chrona-gray-400">
          <p>Eine Bestätigung wurde an {contactInfo.email} gesendet.</p>
          <p className="mt-1">Du erhältst 24 Stunden vor deinem Termin eine Erinnerung.</p>
          <p className="mt-2">Bei Fragen kontaktiere bitte direkt {selectedProvider?.name}.</p>
        </div>
      </div>
    </motion.div>
  );
};

export default BookingComplete;

